import { Injectable } from '@angular/core';
import { initialize, LDClient, LDContext } from 'launchdarkly-js-client-sdk';
import { environment } from '../../../environments/environment';

@Injectable()
export class FeatureFlagService {
  private ldClient: LDClient | null = null;
  environment = environment;
  private sdkKey = '';
  private initialized = false;

  constructor() {}

  /**
   * Description - Initialises the connection to the LaucnhDarkly Feature Flag service, reading from the environment the sdkKey for the specific environment on the LaunchDarkly portal.
   * @param {LDContext} context - Context is the object to be passed to the service which it will then use to resolve the flag, it can be anonymous before login and for instance the user after login.
   * @returns {promise} - Returns - promise about if the connection is successful, loggin also when connection is ready or in error
   */
  async initialize(context: LDContext): Promise<void> {
    if (this.initialized) return;

    this.sdkKey = this.environment.launchDarklyKey;
    this.ldClient = initialize(this.sdkKey, context);

    await new Promise<void>((resolve, reject) => {
      this.ldClient.on('ready', () => resolve());
      this.ldClient.on('failed', (e: any) => reject(e));
    });

    this.initialized = true;
  }

  /**
   * Description - Update the connection to LaunchDarkly with a new context.
   * @param {LDContext} context - Context is the object to be passed to the service which it will then use to resolve the flag, it can be anonymous before login and for instance the user after login.
   * @returns {promise} - Returns - promise about if the update is successful, loggin also when update is done or in error
   */
  updateUserContext(context: LDContext): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.ldClient) {
        console.error('LaunchDarkly client is not initialized');
        reject('Client not initialized');
        return;
      }

      this.ldClient
        .identify(context)
        .then(() => {
          console.log('LaunchDarkly - User context updated:', context);
          resolve();
        })
        .catch((err) => {
          console.error('LaunchDarkly - Error updating user context:', err);
          reject(err);
        });
    });
  }

  /**
   * Description - Get the value of a specific Feature Flag.
   *
   * Example - const flagValue = this.featureFlagService.getFlagValue('test-flag', false)
   * @param {string} flagKey - Key of the requested Feature Flag.
   * @param {any} defaultValue - The Default Value use if the Feature Flag cannot be resolved.
   * @returns {any} - Returns - value of the requested Feature Flag.
   */
  getFlagValue(flagKey: string, defaultValue: any): any {
    // Return the value of a feature flag
    if (!this.ldClient) {
      console.warn('LaunchDarkly client is not initialized');
      return defaultValue;
    }
    return this.ldClient.variation(flagKey, defaultValue);
  }

  /**
   * Description - Add a listener on change of a specific Feature Flag.
   *
   * Example - this.featureFlagService.onFlagChange('test-flag', (newValue) => {
   *             console.log('Feature flag updated:', newValue);
   *           });
   * @param {string} flagKey - Key of the requested Feature Flag.
   * @param {any} callback - Callback to do after the Feature Flag is resolved.
   */
  onFlagChange(flagKey: string, callback: (value: any) => void): void {
    // Listener on change of flag value
    if (this.ldClient) {
      this.ldClient.on(`change:${flagKey}`, callback);
    }
  }
}

//? how to use in the code?
// const flagValue = this.featureFlagService.getFlagValue('enable-last-activity-dashboard', false);
// console.log('Feature flag value:', flagValue);
// this.showLastActivity = flagValue;
// this.featureFlagService.onFlagChange('enable-last-activity-dashboard', (newValue) => {
//     console.log('Feature flag updated:', newValue);
//     this.showLastActivity = newValue;
// });
